import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import { 
  Lock, 
  User, 
  Database, 
  Zap, 
  Palette, 
  Users, 
  FileText, 
  BarChart,
  Save,
  RefreshCw,
  Trash2,
  Play,
  Trophy,
  AlertCircle,
  Target
} from "lucide-react";

const AdminPanel = () => {
  const navigate = useNavigate();
  const [authenticated, setAuthenticated] = useState(false);
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  
  // State for all configuration sections
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [agentConfig, setAgentConfig] = useState({
    enabled: true,
    frequency: 60,
    personality: "professional",
    customMessage: ""
  });
  const [uiConfig, setUiConfig] = useState({
    primaryColor: "#3dd9c5",
    secondaryColor: "#e91e8c",
    showLeaderboard: true,
    showAgentActions: true,
    logoUrl: ""
  });
  const [revenueSlider, setRevenueSlider] = useState(0);

  useEffect(() => {
    // Check if already authenticated
    const isAuth = sessionStorage.getItem('ism-admin-auth') === 'true';
    if (isAuth) {
      setAuthenticated(true);
      loadUsers();
    }
  }, []);

  useEffect(() => {
    if (userProfile) {
      const percentage = (userProfile.currentRevenue / userProfile.revenueGoal) * 100;
      setRevenueSlider(percentage);
    }
  }, [userProfile]);

  const handleLogin = () => {
    if (password === "ISMpassword") {
      sessionStorage.setItem('ism-admin-auth', 'true');
      setAuthenticated(true);
      toast.success("Admin access granted");
      loadUsers();
    } else {
      toast.error("Invalid password");
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem('ism-admin-auth');
    setAuthenticated(false);
    toast.info("Logged out");
  };

  const loadUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`);
      setUsers(response.data);
      if (response.data.length > 0) {
        setSelectedUser(response.data[0].userId);
        setUserProfile(response.data[0]);
      }
    } catch (error) {
      console.error("Error loading users:", error);
    }
  };

  const handleUserSelect = (userId) => {
    const user = users.find(u => u.userId === userId);
    setSelectedUser(userId);
    setUserProfile(user);
  };

  const saveUserProfile = async () => {
    setLoading(true);
    try {
      await axios.put(`${API}/users/${selectedUser}`, userProfile);
      toast.success("Profile updated successfully");
      loadUsers();
    } catch (error) {
      console.error("Error saving profile:", error);
      toast.error("Failed to save profile");
    } finally {
      setLoading(false);
    }
  };

  const generateSampleAccounts = async () => {
    setLoading(true);
    try {
      const count = Math.floor(Math.random() * 11) + 10; // 10-20 accounts
      const industries = ["Technology", "Finance", "Healthcare", "Manufacturing", "Retail", "Insurance"];
      const personas = ["CEO", "CTO", "CFO", "VP Sales", "Director of Operations"];
      const statuses = ["prospect", "engaged", "qualified"];
      
      const companies = [
        "Acme Corp", "Global Dynamics", "InnovateTech", "DataStream Analytics", "CloudFirst Inc",
        "Enterprise Solutions", "TechVentures", "Digital Forge", "Quantum Systems", "Apex Industries",
        "Pinnacle Group", "Horizon Technologies", "Vertex Solutions", "Nexus Corp", "Catalyst Ventures"
      ];

      for (let i = 0; i < count; i++) {
        await axios.post(`${API}/accounts`, {
          userId: selectedUser,
          companyName: companies[Math.floor(Math.random() * companies.length)] + ` ${i + 1}`,
          industry: industries[Math.floor(Math.random() * industries.length)],
          targetPersona: personas[Math.floor(Math.random() * personas.length)],
          status: statuses[Math.floor(Math.random() * statuses.length)],
          engagementScore: Math.floor(Math.random() * 100),
          nextAction: "Initial outreach"
        });
      }
      
      toast.success(`Generated ${count} sample accounts`);
    } catch (error) {
      console.error("Error generating accounts:", error);
      toast.error("Failed to generate accounts");
    } finally {
      setLoading(false);
    }
  };

  const generateSamplePipeline = async () => {
    setLoading(true);
    try {
      const count = Math.floor(Math.random() * 11) + 15; // 15-25 deals
      const stages = ["qualified", "discovery", "proposal", "negotiation", "closed-won"];
      
      // Get accounts first
      const accountsRes = await axios.get(`${API}/accounts/${selectedUser}`);
      const accounts = accountsRes.data;
      
      if (accounts.length === 0) {
        toast.error("Generate accounts first");
        setLoading(false);
        return;
      }

      for (let i = 0; i < count; i++) {
        const account = accounts[Math.floor(Math.random() * accounts.length)];
        await axios.post(`${API}/pipeline`, {
          accountId: account.accountId,
          userId: selectedUser,
          stage: stages[Math.floor(Math.random() * stages.length)],
          value: Math.floor(Math.random() * 400000) + 50000,
          probability: Math.floor(Math.random() * 60) + 20
        });
      }
      
      toast.success(`Generated ${count} pipeline opportunities`);
    } catch (error) {
      console.error("Error generating pipeline:", error);
      toast.error("Failed to generate pipeline");
    } finally {
      setLoading(false);
    }
  };

  const clearAllData = async () => {
    if (!window.confirm("Are you sure you want to clear all data? This cannot be undone.")) {
      return;
    }
    
    setLoading(true);
    try {
      // Note: This would require backend endpoints to delete data
      toast.info("Data clearing not fully implemented - would clear accounts, pipeline, actions");
    } catch (error) {
      console.error("Error clearing data:", error);
      toast.error("Failed to clear data");
    } finally {
      setLoading(false);
    }
  };

  const updateRevenueBySlider = async (percentage) => {
    if (!userProfile) return;
    const newRevenue = (userProfile.revenueGoal * percentage) / 100;
    setUserProfile({ ...userProfile, currentRevenue: newRevenue });
  };

  const triggerAgentUpdate = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/agent/auto-activity/${selectedUser}`);
      toast.success("Agent update triggered");
    } catch (error) {
      console.error("Error triggering agent:", error);
      toast.error("Failed to trigger agent");
    } finally {
      setLoading(false);
    }
  };

  const loadScenario = async (scenario) => {
    if (!userProfile) return;
    setLoading(true);
    
    try {
      let updates = {};
      
      switch (scenario) {
        case "crushing-it":
          updates = {
            currentRevenue: userProfile.revenueGoal * 0.95
          };
          break;
        case "behind-pace":
          updates = {
            currentRevenue: userProfile.revenueGoal * 0.40
          };
          break;
        case "mid-quarter":
          updates = {
            currentRevenue: userProfile.revenueGoal * 0.65
          };
          break;
        case "new-user":
          updates = {
            currentRevenue: 0
          };
          break;
      }
      
      await axios.put(`${API}/users/${selectedUser}`, updates);
      toast.success(`Loaded ${scenario} scenario`);
      loadUsers();
    } catch (error) {
      console.error("Error loading scenario:", error);
      toast.error("Failed to load scenario");
    } finally {
      setLoading(false);
    }
  };

  if (!authenticated) {
    return (
      <div className="min-h-screen bg-[#0a0e17] flex items-center justify-center">
        <Card className="bg-[#0f1419] border-gray-800 p-8 w-full max-w-md">
          <div className="flex items-center justify-center mb-6">
            <Lock className="h-12 w-12 text-[#3dd9c5]" />
          </div>
          <h2 className="text-2xl font-bold text-white text-center mb-6">Admin Panel</h2>
          <div className="space-y-4">
            <div>
              <Label className="text-gray-400">Password</Label>
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleLogin()}
                placeholder="Enter admin password"
                className="bg-[#1a1a1b] border-gray-700 text-white"
                data-testid="admin-password"
              />
            </div>
            <Button
              onClick={handleLogin}
              className="w-full bg-[#3dd9c5] hover:bg-[#35c4b5] text-white"
              data-testid="admin-login-btn"
            >
              Login
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0e17]" data-testid="admin-panel">
      {/* Header */}
      <header className="border-b border-gray-800 bg-[#0f1419] p-4">
        <div className="container mx-auto flex items-center justify-between">
          <h1 className="text-2xl font-bold text-white">Admin Configuration Panel</h1>
          <div className="flex items-center gap-4">
            <select
              value={selectedUser || ""}
              onChange={(e) => handleUserSelect(e.target.value)}
              className="bg-[#1a1a1b] border border-gray-700 text-white rounded px-3 py-2"
              data-testid="user-selector"
            >
              {users.map(user => (
                <option key={user.userId} value={user.userId}>
                  {user.companyName}
                </option>
              ))}
            </select>
            <Button
              onClick={() => navigate(`/dashboard/${selectedUser}`)}
              variant="outline"
              className="border-[#3dd9c5] text-[#3dd9c5]"
            >
              View Dashboard
            </Button>
            <Button
              onClick={handleLogout}
              variant="outline"
              className="border-gray-700 text-gray-400"
            >
              Logout
            </Button>
          </div>
        </div>
      </header>

      <div className="container mx-auto p-6">
        <Tabs defaultValue="profile" className="space-y-6">
          <TabsList className="bg-[#0f1419] border border-gray-800">
            <TabsTrigger value="profile" className="data-[state=active]:bg-[#3dd9c5]"><User className="h-4 w-4 mr-2" />Profile</TabsTrigger>
            <TabsTrigger value="data" className="data-[state=active]:bg-[#3dd9c5]"><Database className="h-4 w-4 mr-2" />Data</TabsTrigger>
            <TabsTrigger value="agent" className="data-[state=active]:bg-[#3dd9c5]"><Zap className="h-4 w-4 mr-2" />Agent</TabsTrigger>
            <TabsTrigger value="ui" className="data-[state=active]:bg-[#3dd9c5]"><Palette className="h-4 w-4 mr-2" />UI</TabsTrigger>
            <TabsTrigger value="scenarios" className="data-[state=active]:bg-[#3dd9c5]"><FileText className="h-4 w-4 mr-2" />Scenarios</TabsTrigger>
          </TabsList>

          {/* User Profile Quick Edit */}
          <TabsContent value="profile">
            {userProfile && (
              <Card className="bg-[#0f1419] border-gray-800 p-6">
                <h2 className="text-xl font-bold text-white mb-6">User Profile Quick Edit</h2>
                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <Label className="text-gray-400">Company Name</Label>
                    <Input
                      value={userProfile.companyName}
                      onChange={(e) => setUserProfile({...userProfile, companyName: e.target.value})}
                      className="bg-[#1a1a1b] border-gray-700 text-white"
                    />
                  </div>
                  <div>
                    <Label className="text-gray-400">Revenue Goal</Label>
                    <Input
                      type="number"
                      value={userProfile.revenueGoal}
                      onChange={(e) => setUserProfile({...userProfile, revenueGoal: parseFloat(e.target.value)})}
                      className="bg-[#1a1a1b] border-gray-700 text-white"
                    />
                  </div>
                  <div>
                    <Label className="text-gray-400">Current Revenue</Label>
                    <Input
                      type="number"
                      value={userProfile.currentRevenue}
                      onChange={(e) => setUserProfile({...userProfile, currentRevenue: parseFloat(e.target.value)})}
                      className="bg-[#1a1a1b] border-gray-700 text-white"
                    />
                  </div>
                  <div>
                    <Label className="text-gray-400">Revenue % (Slider)</Label>
                    <div className="flex items-center gap-4">
                      <input
                        type="range"
                        min="0"
                        max="100"
                        value={revenueSlider}
                        onChange={(e) => {
                          setRevenueSlider(e.target.value);
                          updateRevenueBySlider(e.target.value);
                        }}
                        className="flex-1"
                      />
                      <span className="text-white font-semibold w-16">{Math.round(revenueSlider)}%</span>
                    </div>
                  </div>
                  <div className="col-span-2">
                    <Label className="text-gray-400">Products</Label>
                    <Textarea
                      value={userProfile.products}
                      onChange={(e) => setUserProfile({...userProfile, products: e.target.value})}
                      className="bg-[#1a1a1b] border-gray-700 text-white min-h-[100px]"
                    />
                  </div>
                  <div className="col-span-2">
                    <Label className="text-gray-400">ICP</Label>
                    <Textarea
                      value={userProfile.icp}
                      onChange={(e) => setUserProfile({...userProfile, icp: e.target.value})}
                      className="bg-[#1a1a1b] border-gray-700 text-white min-h-[100px]"
                    />
                  </div>
                </div>
                <div className="flex gap-3 mt-6">
                  <Button
                    onClick={saveUserProfile}
                    disabled={loading}
                    className="bg-[#3dd9c5] hover:bg-[#35c4b5] text-white"
                    data-testid="save-profile-btn"
                  >
                    <Save className="h-4 w-4 mr-2" />
                    Save Changes
                  </Button>
                  <Button
                    onClick={loadUsers}
                    variant="outline"
                    className="border-gray-700 text-gray-400"
                  >
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Reset
                  </Button>
                </div>
              </Card>
            )}
          </TabsContent>

          {/* Demo Data Generator */}
          <TabsContent value="data">
            <Card className="bg-[#0f1419] border-gray-800 p-6">
              <h2 className="text-xl font-bold text-white mb-6">Demo Data Generator</h2>
              <div className="space-y-4">
                <Button
                  onClick={generateSampleAccounts}
                  disabled={loading}
                  className="w-full bg-[#3dd9c5] hover:bg-[#35c4b5] text-white justify-start"
                  data-testid="gen-accounts-btn"
                >
                  <Database className="h-4 w-4 mr-2" />
                  Generate Sample Accounts (10-20)
                </Button>
                <Button
                  onClick={generateSamplePipeline}
                  disabled={loading}
                  className="w-full bg-[#3dd9c5] hover:bg-[#35c4b5] text-white justify-start"
                  data-testid="gen-pipeline-btn"
                >
                  <BarChart className="h-4 w-4 mr-2" />
                  Generate Sample Pipeline (15-25)
                </Button>
                <Button
                  onClick={clearAllData}
                  disabled={loading}
                  variant="destructive"
                  className="w-full justify-start"
                  data-testid="clear-data-btn"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Clear All Data
                </Button>
              </div>
            </Card>
          </TabsContent>

          {/* Agent Behavior Controls */}
          <TabsContent value="agent">
            <Card className="bg-[#0f1419] border-gray-800 p-6">
              <h2 className="text-xl font-bold text-white mb-6">Agent Behavior Controls</h2>
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <Label className="text-gray-400">Enable Autonomous Updates</Label>
                  <Switch
                    checked={agentConfig.enabled}
                    onCheckedChange={(checked) => setAgentConfig({...agentConfig, enabled: checked})}
                  />
                </div>
                <div>
                  <Label className="text-gray-400">Activity Frequency (seconds)</Label>
                  <div className="flex items-center gap-4 mt-2">
                    <input
                      type="range"
                      min="10"
                      max="300"
                      value={agentConfig.frequency}
                      onChange={(e) => setAgentConfig({...agentConfig, frequency: e.target.value})}
                      className="flex-1"
                    />
                    <span className="text-white font-semibold w-16">{agentConfig.frequency}s</span>
                  </div>
                </div>
                <div>
                  <Label className="text-gray-400">Agent Personality</Label>
                  <select
                    value={agentConfig.personality}
                    onChange={(e) => setAgentConfig({...agentConfig, personality: e.target.value})}
                    className="w-full bg-[#1a1a1b] border border-gray-700 text-white rounded px-3 py-2 mt-2"
                  >
                    <option value="professional">Professional</option>
                    <option value="aggressive">Aggressive</option>
                    <option value="consultative">Consultative</option>
                  </select>
                </div>
                <div>
                  <Label className="text-gray-400">Custom Agent Message</Label>
                  <Textarea
                    value={agentConfig.customMessage}
                    onChange={(e) => setAgentConfig({...agentConfig, customMessage: e.target.value})}
                    placeholder="Enter a custom message for the agent to display..."
                    className="bg-[#1a1a1b] border-gray-700 text-white mt-2"
                  />
                </div>
                <Button
                  onClick={triggerAgentUpdate}
                  disabled={loading}
                  className="w-full bg-[#e91e8c] hover:bg-[#d01a7d] text-white"
                  data-testid="trigger-agent-btn"
                >
                  <Play className="h-4 w-4 mr-2" />
                  Trigger Agent Update Now
                </Button>
              </div>
            </Card>
          </TabsContent>

          {/* UI Customization */}
          <TabsContent value="ui">
            <Card className="bg-[#0f1419] border-gray-800 p-6">
              <h2 className="text-xl font-bold text-white mb-6">UI Customization</h2>
              <div className="space-y-6">
                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <Label className="text-gray-400">Primary Accent Color</Label>
                    <div className="flex gap-3 mt-2">
                      <input
                        type="color"
                        value={uiConfig.primaryColor}
                        onChange={(e) => setUiConfig({...uiConfig, primaryColor: e.target.value})}
                        className="w-20 h-10 rounded cursor-pointer"
                      />
                      <Input
                        value={uiConfig.primaryColor}
                        onChange={(e) => setUiConfig({...uiConfig, primaryColor: e.target.value})}
                        className="bg-[#1a1a1b] border-gray-700 text-white"
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="text-gray-400">Secondary Accent Color</Label>
                    <div className="flex gap-3 mt-2">
                      <input
                        type="color"
                        value={uiConfig.secondaryColor}
                        onChange={(e) => setUiConfig({...uiConfig, secondaryColor: e.target.value})}
                        className="w-20 h-10 rounded cursor-pointer"
                      />
                      <Input
                        value={uiConfig.secondaryColor}
                        onChange={(e) => setUiConfig({...uiConfig, secondaryColor: e.target.value})}
                        className="bg-[#1a1a1b] border-gray-700 text-white"
                      />
                    </div>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <Label className="text-gray-400">Show Peer Leaderboard</Label>
                  <Switch
                    checked={uiConfig.showLeaderboard}
                    onCheckedChange={(checked) => setUiConfig({...uiConfig, showLeaderboard: checked})}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <Label className="text-gray-400">Show Agent Actions</Label>
                  <Switch
                    checked={uiConfig.showAgentActions}
                    onCheckedChange={(checked) => setUiConfig({...uiConfig, showAgentActions: checked})}
                  />
                </div>
                <div>
                  <Label className="text-gray-400">Custom Logo URL</Label>
                  <Input
                    value={uiConfig.logoUrl}
                    onChange={(e) => setUiConfig({...uiConfig, logoUrl: e.target.value})}
                    placeholder="https://example.com/logo.png"
                    className="bg-[#1a1a1b] border-gray-700 text-white mt-2"
                  />
                </div>
                <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500/50">
                  Note: UI changes require page refresh to take effect
                </Badge>
              </div>
            </Card>
          </TabsContent>

          {/* Scenario Presets */}
          <TabsContent value="scenarios">
            <Card className="bg-[#0f1419] border-gray-800 p-6">
              <h2 className="text-xl font-bold text-white mb-6">Scenario Presets</h2>
              <div className="grid grid-cols-2 gap-4">
                <Button
                  onClick={() => loadScenario('crushing-it')}
                  disabled={loading}
                  className="h-24 flex-col bg-green-500/10 hover:bg-green-500/20 border border-green-500/30 text-white"
                  data-testid="scenario-crushing"
                >
                  <Trophy className="h-6 w-6 mb-2 text-green-400" />
                  <span className="font-semibold">Crushing It</span>
                  <span className="text-xs text-gray-400">95% to goal</span>
                </Button>
                <Button
                  onClick={() => loadScenario('behind-pace')}
                  disabled={loading}
                  className="h-24 flex-col bg-red-500/10 hover:bg-red-500/20 border border-red-500/30 text-white"
                  data-testid="scenario-behind"
                >
                  <AlertCircle className="h-6 w-6 mb-2 text-red-400" />
                  <span className="font-semibold">Behind Pace</span>
                  <span className="text-xs text-gray-400">40% to goal</span>
                </Button>
                <Button
                  onClick={() => loadScenario('mid-quarter')}
                  disabled={loading}
                  className="h-24 flex-col bg-yellow-500/10 hover:bg-yellow-500/20 border border-yellow-500/30 text-white"
                  data-testid="scenario-mid-quarter"
                >
                  <Target className="h-6 w-6 mb-2 text-yellow-400" />
                  <span className="font-semibold">Mid-Quarter Push</span>
                  <span className="text-xs text-gray-400">65% to goal</span>
                </Button>
                <Button
                  onClick={() => loadScenario('new-user')}
                  disabled={loading}
                  className="h-24 flex-col bg-gray-500/10 hover:bg-gray-500/20 border border-gray-500/30 text-white"
                  data-testid="scenario-new-user"
                >
                  <User className="h-6 w-6 mb-2 text-gray-400" />
                  <span className="font-semibold">New User</span>
                  <span className="text-xs text-gray-400">Fresh start</span>
                </Button>
              </div>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default AdminPanel;
